import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSocket } from '../../realtime/context/SocketContext';
import { useAuth } from '../../auth/context/AuthContext';

export default function ChatPage() {
  const { user } = useAuth();
  const socket = useSocket();
  const [messages, setMessages] = useState([]);
  const [adminId, setAdminId] = useState(null);
  const [text, setText] = useState('');
  const [botTyping, setBotTyping] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get('/api/chat/admin-id').then((r) => {
      if (r.data) {
        setAdminId(r.data.id);
        axios.get(`/api/chat/messages/${r.data.id}`).then((res) => setMessages(res.data)).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      if (!adminId) return;
      const belongsToThread =
        (msg.sender_id === adminId && msg.receiver_id === user?.id) ||
        (msg.sender_id === user?.id && msg.receiver_id === adminId);

      if (!belongsToThread) return;
      setMessages((prev) => [...prev, msg]);
      if (msg.sender_id === adminId) {
        socket.emit('chat:read', { senderId: adminId });
      }
    };
    const typingHandler = ({ typing }) => setBotTyping(typing);

    socket.on('chat:message', handler);
    socket.on('chat:bot_typing', typingHandler);
    return () => {
      socket.off('chat:message', handler);
      socket.off('chat:bot_typing', typingHandler);
    };
  }, [socket, adminId, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, botTyping]);

  const send = (message, optionId = null) => {
    const msg = typeof message === 'string' ? message : text.trim();
    if (!msg || !adminId) return;
    if (!socket?.connected) return toast.error('Chat is reconnecting. Please try again.');
    socket.emit('chat:send', { receiverId: adminId, message: msg, optionId });
    setText('');
  };

  const selectOption = (option) => {
    send(option.label, option.id);
  };

  return (
    <div className="chat-page">
      <div className="page-header">
        <Link to="/home" className="back-btn">&larr;</Link>
        <div>
          <h1>Chat Support</h1>
          <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Powered by Smaiz Innovations</p>
        </div>
        <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Online</span>
      </div>
      <div className="chat-scroll">
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 40 }}>
            <p style={{ fontSize: 32, marginBottom: 8 }}>Chat</p>
            <p>Start a conversation with our support team</p>
          </div>
        )}
        {messages.map((msg, i) => {
          const mine = msg.sender_id === user?.id && msg.sender_type !== 'bot';
          const isBot = msg.sender_type === 'bot';
          const options = msg.bot_meta?.options || null;
          const isLatestBot = isBot && i === messages.map((m, idx) => (m.sender_type === 'bot' ? idx : -1)).filter((x) => x >= 0).slice(-1)[0];
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              {isBot && (
                <p style={{ fontSize: 10, fontWeight: 700, color: '#1A6B8A', marginBottom: 3, marginLeft: 4, letterSpacing: 0.3 }}>
                  D2D ASSISTANT | POWERED BY SMAIZ INNOVATIONS
                </p>
              )}
              <div className={`chat-bubble chat-msg-enter ${mine ? 'mine' : isBot ? 'bot' : 'theirs'}`}>
                {msg.message.split('\n').map((line, li) => (
                  <span key={li}>
                    {line.split(/\*(.*?)\*/g).map((part, pi) =>
                      pi % 2 === 1 ? <strong key={pi}>{part}</strong> : part
                    )}
                    {li < msg.message.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </div>
              {isLatestBot && options && (
                <div className="bot-options">
                  {options.map((opt) => (
                    <button key={opt.id} className="bot-option-btn" onClick={() => selectOption(opt)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <p className="chat-time" style={{ marginTop: 2, marginBottom: 6 }}>
                {new Date(msg.sent_at).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          );
        })}
        {botTyping && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#1A6B8A', marginBottom: 3, marginLeft: 4, letterSpacing: 0.3 }}>
              D2D ASSISTANT
            </p>
            <div className="chat-bubble bot chat-typing-bubble">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ padding: '0 16px 8px', fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
        Chat experience powered by Smaiz Innovations
      </div>
      <div className="chat-composer">
        <input className="input-field" style={{ flex: 1 }} placeholder="Type a message..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} />
        <button className="btn btn-primary" onClick={send} disabled={!text.trim()}>Send</button>
      </div>
    </div>
  );
}
